/**
 * Screen registry — the single source of truth describing every console screen
 * to the AI assistant. Each entry grounds the model in (a) what the screen is
 * for, (b) what the user can do there, (c) which MCP tools back those actions,
 * and (d) good starter questions.
 *
 * The registry is client-owned: the chat composable resolves the entry for the
 * current route and sends only that compact doc (plus live page context) to the
 * orchestrator, so the server never has to embed the whole site map in every
 * prompt. This keeps prompts small and the knowledge co-located with the UI.
 */
import { GENERATED_SCREENS } from './screens.generated'

export interface ScreenDoc {
  /** Stable id, also used as the page-context screenId. */
  id: string
  /** Route matcher: exact path or a prefix ending in `*`. */
  match: string
  title: string
  /** One or two sentences: what this screen is and what it shows. */
  summary: string
  /** Concrete things the user can do here (plain language). */
  capabilities: string[]
  /** MCP tool names relevant to this screen (helps the model pick tools). */
  relatedTools: string[]
  /** Click-to-ask starter questions shown when the widget opens on this screen. */
  suggestedPrompts: string[]
}

export const SCREENS: ScreenDoc[] = [
  {
    id: 'dashboard',
    match: '/',
    title: 'Dashboard',
    summary:
      'Account overview: recent instances, VPCs, VPN gateways and quick actions.',
    capabilities: [
      'See an overview of your cloud resources',
      'Jump to instances, networking and billing',
    ],
    relatedTools: ['vm_list', 'billing_subscriptions'],
    suggestedPrompts: [
      'Give me a summary of my account',
      'How many instances do I have running?',
      'What am I currently being billed for?',
    ],
  },
  {
    id: 'instances',
    match: '/instances',
    title: 'Instances',
    summary:
      'List of all virtual machines (cloud instances) with their status and specs.',
    capabilities: [
      'View every instance and its status',
      'Create, start, stop or reboot instances',
    ],
    relatedTools: ['vm_list', 'vm_status', 'vm_power'],
    suggestedPrompts: [
      'List my instances and their status',
      'Which of my instances are stopped?',
      'Reboot my instance for me',
    ],
  },
  {
    id: 'instance-detail',
    match: '/instances/*',
    title: 'Instance details',
    summary:
      'Details, power controls, hardware, monitoring and networking for a single instance.',
    capabilities: [
      'Inspect specs, IPs and live status',
      'Start / stop / reboot this instance',
      'Review CPU, memory, disk and network metrics',
    ],
    relatedTools: ['vm_get', 'vm_status', 'vm_metrics', 'vm_power'],
    suggestedPrompts: [
      'Why is this instance using so much CPU?',
      'What are the specs of this instance?',
      'Reboot this instance',
    ],
  },
  {
    id: 'billing',
    match: '/billing',
    title: 'Billing',
    summary:
      'Subscriptions, balance, usage and invoices for your cloud account.',
    capabilities: [
      'Review subscriptions and balances',
      'Inspect the credit/debit ledger and invoices',
    ],
    relatedTools: ['billing_subscriptions', 'billing_subscription_get', 'billing_ledger'],
    suggestedPrompts: [
      'Explain my current bill',
      'What is my available balance?',
      'Show my recent ledger entries',
    ],
  },
  /*
   * Checkout is the single most valuable page in the app, and until now it
   * matched nothing: `/billing` is an exact pattern, so `/billing/checkout`
   * fell through to the generic fallback and the assistant greeted a customer
   * mid-purchase with "What can you help me with?".
   */
  {
    id: 'checkout',
    match: '/billing/checkout',
    title: 'Checkout',
    summary:
      'Ordering a new cloud instance: plan and term, datacenter, operating system, storage, add-ons, coupon and payment method.',
    capabilities: [
      'Explain what a plan includes and how the term affects the price',
      'Explain storage, bandwidth allowance and add-ons',
      'Explain the accepted payment methods and when the instance is created',
    ],
    relatedTools: ['billing_subscriptions'],
    suggestedPrompts: [
      'Which plan fits my website?',
      'What does this term change on the price?',
      'Which payment methods do you accept?',
    ],
  },
  {
    id: 'vpn-checkout',
    match: '/vpn-gateway/checkout',
    title: 'VPN Gateway checkout',
    summary: 'Ordering a VPN gateway: location, capacity and billing term.',
    capabilities: [
      'Explain what a VPN gateway does and which location to pick',
      'Explain how users and capacity are billed',
    ],
    relatedTools: [],
    suggestedPrompts: [
      'What is a VPN gateway for?',
      'Which location should I choose?',
      'How many users does this support?',
    ],
  },
  {
    id: 'network-dns',
    match: '/network/dns',
    title: 'DNS',
    summary: 'Managed DNS zones and records.',
    capabilities: ['View DNS zones and records', 'Create zones and records'],
    relatedTools: ['dns_zone_list', 'dns_record_list'],
    suggestedPrompts: [
      'List my DNS zones',
      'How do I add an A record?',
      'Check the health of my zone',
    ],
  },
  {
    id: 'network-firewall',
    match: '/network/firewall',
    title: 'Firewall',
    summary: 'Cloud firewalls and their inbound/outbound rules.',
    capabilities: ['View firewalls and rules', 'Create firewalls and rules'],
    relatedTools: ['firewall_list', 'firewall_get'],
    suggestedPrompts: [
      'List my firewalls',
      'How do I allow inbound HTTPS?',
      'What rules are on this firewall?',
    ],
  },
  {
    id: 'console',
    match: '/console/*',
    title: 'VM Console (VNC)',
    summary:
      'Live VNC console of an instance — the user sees the VM screen exactly as' +
      ' a physical monitor would show it. A screenshot of the current screen is' +
      ' attached to questions so the assistant can read on-screen output.',
    capabilities: [
      'View and interact with the VM screen (keyboard/mouse)',
      'Send Ctrl+Alt+Del, reboot, reconnect, go fullscreen',
      'Ask the assistant to analyze what the screen is showing',
    ],
    relatedTools: ['vm_status', 'vm_power'],
    suggestedPrompts: [
      'What is my screen showing right now?',
      'Explain the error on my console screen',
      "My server won't boot — help me troubleshoot",
    ],
  },
  {
    id: 'settings-api-tokens',
    match: '/settings/api-tokens',
    title: 'API Tokens',
    summary:
      'Manage personal access tokens that authenticate API/MCP requests — including the one the assistant uses.',
    capabilities: [
      'Create, roll and revoke API tokens',
      'See which scopes each token grants',
    ],
    relatedTools: [],
    suggestedPrompts: [
      'What is an API token used for?',
      'Which scopes should I grant for read-only access?',
      'How do I revoke a token?',
    ],
  },
]

const FALLBACK: ScreenDoc = {
  id: 'general',
  match: '*',
  title: 'MasterDaWeb Console',
  summary: 'The MasterDaWeb cloud console.',
  capabilities: ['Manage instances, networking and billing'],
  relatedTools: ['vm_list', 'billing_subscriptions'],
  suggestedPrompts: [
    'What can you help me with?',
    'Give me a summary of my account',
    'List my instances',
  ],
}

/** Strip locale prefix + trailing slash so `/en-GB/instances/5` matches `/instances/*`. */
function normalizePath(path: string): string {
  const withoutLocale = path.replace(/^\/[a-z]{2}(-[A-Z]{2})?(?=\/)/, '')
  const trimmed = withoutLocale.replace(/\/+$/, '')
  return trimmed || '/'
}

function matches(pattern: string, path: string): boolean {
  if (pattern === '*') return true
  if (pattern.endsWith('/*')) {
    const base = pattern.slice(0, -2)
    return path === base || path.startsWith(`${base}/`)
  }
  return pattern === path
}

/** Resolve the most specific screen doc for a route path. */
export function resolveScreen(path: string): ScreenDoc {
  const p = normalizePath(path)
  // Prefer the longest (most specific) matching pattern.
  const candidates = SCREENS.filter((s) => matches(s.match, p)).sort(
    (a, b) => b.match.length - a.match.length,
  )
  return candidates[0] ?? FALLBACK
}

export function suggestedPromptsForRoute(path: string): string[] {
  return resolveScreen(path).suggestedPrompts
}

/** One screen in the navigation site map sent to the assistant. */
export interface SiteMapScreen {
  route: string
  title: string
  parent: string | null
  summary?: string
}

/**
 * The COMPLETE navigable site map: every route harvested from the pages
 * (full feature coverage), enriched with the curated summary for the key
 * screens. This is what lets the assistant give step-by-step directions for any
 * feature, not just the hand-curated few.
 */
export function fullSiteMap(): SiteMapScreen[] {
  return GENERATED_SCREENS.map((g) => {
    const curated = resolveScreen(g.route)
    const hasCurated = curated.id !== 'general'
    return {
      route: g.route,
      title: hasCurated ? curated.title : g.title,
      parent: g.parent,
      summary: hasCurated ? curated.summary : undefined,
    }
  })
}
